/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * POC COPY — logic mirrored from
 *   cds-ai-service/frontend/src/components/assistant/DashboardAssistantDrawerContent.jsx
 *   (parseFollowUpQuestions / parseTrailingFollowUpJson helpers).
 * See ./README.md for the extraction plan.
 */

const MAX_FOLLOW_UP_QUESTIONS = 7;
const DELIMITER = '---SUGGESTIONS---';

const parseDelimiterBlock = raw => {
  const idx = raw.indexOf(DELIMITER);

  if (idx === -1) {
    return null;
  }

  const message = raw.slice(0, idx).trim();
  const afterDelimiter = raw.slice(idx + DELIMITER.length).trim();

  if (!afterDelimiter) {
    return { message, suggestions: [] };
  }

  try {
    const parsed = JSON.parse(afterDelimiter);

    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.suggestions)) {
      const suggestions = parsed.suggestions.filter(s => typeof s === 'string');

      return { message, suggestions };
    }
  } catch {
    // Fall through to empty suggestions.
  }

  return { message, suggestions: [] };
};

const parseTrailingFollowUpJson = rawText => {
  if (typeof rawText !== 'string' || !rawText.trim()) {
    return null;
  }

  const trimmed = rawText.trim();
  const jsonAtEndMatch = trimmed.match(/(\{[\s\S]*"followUpQuestions"\s*:\s*\[[\s\S]*?\][\s\S]*\})\s*$/);

  if (!jsonAtEndMatch) {
    return null;
  }

  let parsed;

  try {
    parsed = JSON.parse(jsonAtEndMatch[1]);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.followUpQuestions)) {
    return null;
  }

  const followUpQuestions = parsed.followUpQuestions
    .filter(question => typeof question === 'string' && question.trim().length > 0)
    .map(question => question.trim())
    .slice(0, MAX_FOLLOW_UP_QUESTIONS);

  if (followUpQuestions.length === 0) {
    return null;
  }

  const displayText = trimmed.slice(0, jsonAtEndMatch.index).trim();

  return { displayText, followUpQuestions };
};

export const parseFollowUpQuestions = rawResponse => {
  if (typeof rawResponse !== 'string') {
    return { displayText: String(rawResponse ?? ''), followUpQuestions: [] };
  }

  const trimmed = rawResponse.trim();

  if (!trimmed) {
    return { displayText: trimmed, followUpQuestions: [] };
  }

  const delimiterResult = parseDelimiterBlock(trimmed);

  if (delimiterResult && delimiterResult.suggestions.length > 0) {
    const followUpQuestions = delimiterResult.suggestions
      .filter(question => typeof question === 'string' && question.trim().length > 0)
      .map(question => question.trim())
      .slice(0, MAX_FOLLOW_UP_QUESTIONS);

    return { displayText: delimiterResult.message, followUpQuestions };
  }

  const jsonBlockRe = /\n*```(?:json|JSON)\s*\n([\s\S]*?)\n```\s*$/;
  const match = trimmed.match(jsonBlockRe);

  if (!match) {
    const parsedTrailingJson = parseTrailingFollowUpJson(trimmed);

    if (parsedTrailingJson) {
      return parsedTrailingJson;
    }

    return { displayText: trimmed, followUpQuestions: [] };
  }

  let parsed;

  try {
    parsed = JSON.parse(match[1].trim());
  } catch {
    return { displayText: trimmed, followUpQuestions: [] };
  }

  if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.followUpQuestions)) {
    return { displayText: trimmed, followUpQuestions: [] };
  }

  const followUpQuestions = parsed.followUpQuestions
    .filter(question => typeof question === 'string' && question.trim().length > 0)
    .map(question => question.trim())
    .slice(0, MAX_FOLLOW_UP_QUESTIONS);
  const displayText = trimmed.slice(0, match.index).trim();

  return { displayText, followUpQuestions };
};

export { MAX_FOLLOW_UP_QUESTIONS };
